import { forkJoin } from 'rxjs'

import { HttpParams } from '@angular/common/http'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { CollectionViewGroup, Point, PropertyGroupDescription, SortDescription } from '@grapecity/wijmo'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellType, DataMap, FormatItemEventArgs, GroupRow } from '@grapecity/wijmo.grid'

import {
    BaseComponent,
    FabBoard,
    FabHold,
    FabHoldReason,
    FabHoldReleaseRequest,
    FabJob,
    FabPiece,
    FabProcessStep,
    FeatureType,
    Grid,
    ListItem,
    replaceListItemsIfChanged,
    ModalEditMode,
    ModalSize,
    User,
} from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridColumnGroupComponent } from '../../shared/reusable-components/grid-column-group/grid-column-group.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { FabHoldComponent, FabHoldModalParent } from '../fab-holds/fab-hold.component'
import { FabPieceComponent } from './fab-piece.component'

@Component({
    selector: 'app-fab-board',
    templateUrl: './fab-board.component.html',
    standalone: true,
    imports: [
        FormsModule,
        WjInputModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        GridColumnGroupComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
    ],
})
export class FabBoardComponent extends BaseComponent {
    grid: Grid<FabPiece>
    jobs: FabJob[] = []
    jobOptions: ListItem<string>[] = []
    selectedJobId = ''
    private appliedJobId = ''
    processSteps: FabProcessStep[] = []
    activeSteps: FabProcessStep[] = []
    holdReasons: FabHoldReason[] = []
    userList: ListItem<string>[] = []
    yesNoMap = new DataMap([new ListItem(true, 'Yes'), new ListItem(false, 'No')], 'value', 'display')
    holdActionLabel = 'Place on hold'
    showArchived = false
    canRestoreSelected = false
    private allPieces: FabPiece[] = []

    constructor() {
        super()
        this.grid = new Grid<FabPiece>(this.services, 'id', this)
            .setupModalEditing(this.services.data.fabPieces, 'Piece', FabPieceComponent, () => this.newPiece(), this, ModalSize.extraLarge)
            .setupDeleteConfirm(
                'Confirm Archive',
                'Archive the selected item? It will be hidden from the board. You can show archived records and restore it later.',
                'Confirm Archive',
                'Archive the selected items? They will be hidden from the board. You can show archived records and restore them later.',
            )
            .withColumnFilters()
            .wrapColumnHeaders(110)
            .filterBy(item => [
                item.markCode,
                item.name,
                item.description,
                item.jobDisplayName,
                item.scope,
                item.currentStepName,
                item.nextStepName,
                item.riskLevel,
                item.holdReasonName,
            ])
            .isReadOnly(true)

        this.grid.initialized.on(() => {
            this.applyJobGrouping()
            this.grid.flexGrid.formatItem.addHandler(this.formatItem.bind(this))
        })
        this.grid.selectionChanged.on(() => this.updateHoldActionLabel())

        this.grid.itemAdded.on(() => this.refresh())
        this.grid.itemEdited.on(() => this.refresh())
        this.grid.itemsDeleted.on(() => this.refresh())

        this.services.data.users.queryAllByAction('GetForLookup').subscribe({
            next: (users: User[]) => {
                this.userList = users.filter(u => u.active).map(u => new ListItem(u.id, u.displayName))
            },
        })

        this.refresh()
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.FabBoardEdit])
    }

    get canPlaceHold() {
        return this.services.authentication.hasFeature([FeatureType.FabBoardEdit, FeatureType.FabHoldsEdit])
    }

    newPiece(): FabPiece {
        const piece = new FabPiece()
        piece.id = crypto.randomUUID()
        piece.quantity = 1
        piece.quantityShipped = 0
        piece.isRework = false
        piece.isStock = false
        piece.status = 'Active'
        piece.isOnHold = false
        piece.percentComplete = 0
        piece.steps = []
        piece.notes = []
        piece.imageUrls = []
        piece.imageFileNames = []
        piece.drawingFileNames = ''
        piece.scope = 'Misc Metals'
        piece.archived = false
        if (this.selectedJobId) piece.fabJobId = this.selectedJobId
        return piece
    }

    onJobChanged() {
        if (this.selectedJobId === this.appliedJobId) return
        this.refresh()
    }

    placeOnHold() {
        const piece = this.selectedPiece()
        if (!piece) {
            this.services.notification.error('Select a piece first.')
            return
        }
        if (!this.canPlaceHold) {
            this.services.notification.error('You do not have permission to place holds.')
            return
        }

        if (piece.openHoldId) {
            this.services.data
                .get<FabHold>(`api/FabHold/${piece.openHoldId}`, new HttpParams().set('id', piece.openHoldId), FabHold)
                .subscribe({
                    next: hold => {
                        if (hold.startedOn) hold.startedOn = new Date(hold.startedOn)
                        if (hold.expectedReleaseDate) hold.expectedReleaseDate = new Date(hold.expectedReleaseDate)
                        this.openHoldModal(piece, hold, ModalEditMode.editing)
                    },
                    error: () => this.services.notification.error('Failed to load hold.'),
                })
            return
        }

        const hold = new FabHold()
        hold.id = crypto.randomUUID()
        hold.fabPieceId = piece.id
        hold.startedOn = new Date()
        hold.isOpen = true
        hold.daysHeld = 0
        if (piece.nextStepId) hold.fabProcessStepId = piece.nextStepId
        this.openHoldModal(piece, hold, ModalEditMode.adding)
    }

    private openHoldModal(piece: FabPiece, hold: FabHold, mode: ModalEditMode) {
        const reasonOptions = this.holdReasons
            .filter(r => r.active || r.id === hold.fabHoldReasonId)
            .map(r => new ListItem(r.id, r.name))
        const parent: FabHoldModalParent = {
            pieceOptions: [new ListItem(piece.id, `${piece.markCode ? piece.markCode + ' — ' : ''}${piece.name}`)],
            reasonOptions,
            stepOptions: this.activeSteps.map(s => new ListItem(s.id, s.name)),
            userList: this.userList,
        }

        const ref = this.services.modals.open(FabHoldComponent, ModalSize.large)
        const modal: FabHoldComponent = ref.componentInstance
        modal.initialize({
            initializeParams: parent,
            target: hold,
            mode,
            repository: this.services.data.fabHolds,
            typeName: 'Hold',
        })
        ref.result.then(
            () => {
                this.services.notification.success(mode === ModalEditMode.editing ? 'Hold updated.' : 'Hold placed.')
                this.refresh()
            },
            () => undefined,
        )
    }

    private selectedPiece(): FabPiece | null {
        const piece = this.grid.selectedItem as FabPiece
        return piece?.id ? piece : null
    }

    private selectedPieces(): FabPiece[] {
        const rows = this.grid.flexGrid?.selectedRows ?? []
        const fromRows = rows.map(r => r.dataItem as FabPiece).filter(p => !!p?.id)
        if (fromRows.length) return fromRows
        const one = this.selectedPiece()
        return one ? [one] : []
    }

    private updateHoldActionLabel() {
        this.holdActionLabel = this.selectedPiece()?.openHoldId ? 'Edit Hold' : 'Place on hold'
        this.canRestoreSelected = this.selectedPieces().some(p => p.archived)
    }

    toggleShowArchived() {
        this.showArchived = !this.showArchived
        this.applyPieceVisibility()
    }

    restoreSelected() {
        const items = this.selectedPieces().filter(p => p.archived)
        if (!items.length) {
            this.services.notification.error('Select an archived piece to restore.')
            return
        }

        const message =
            items.length > 1
                ? `Restore ${items.length} archived pieces to the board?`
                : 'Restore the selected piece to the board? Finished pieces stay in this list until a process step is cleared.'
        this.services.modals
            .confirm('Confirm Restore', message)
            .then(() => {
                const requests = items.map(item =>
                    this.services.data.post('api/FabPiece/Restore', {}, new HttpParams().set('id', item.id)),
                )
                forkJoin(requests).subscribe({
                    next: () => {
                        this.services.notification.success(items.length > 1 ? 'Pieces restored.' : 'Piece restored.')
                        this.refresh()
                    },
                    error: () => this.services.notification.error('Failed to restore one or more pieces.'),
                })
            })
            .catch(() => undefined)
    }

    permanentDeleteSelected() {
        const items = this.selectedPieces()
        if (!items.length) {
            this.services.notification.error('Select a piece to delete.')
            return
        }

        const message =
            items.length > 1
                ? `Permanently delete ${items.length} pieces? This cannot be undone.`
                : 'Permanently delete the selected piece? This cannot be undone.'
        this.services.modals
            .confirm('Confirm Permanent Delete', message)
            .then(() => {
                const requests = items.map(item =>
                    this.services.data.http.delete(`api/FabPiece/PermanentDelete?id=${encodeURIComponent(item.id)}`),
                )
                forkJoin(requests).subscribe({
                    next: () => {
                        this.services.notification.success(items.length > 1 ? 'Pieces deleted.' : 'Piece deleted.')
                        this.refresh()
                    },
                    error: () => this.services.notification.error('Failed to delete one or more pieces.'),
                })
            })
            .catch(() => undefined)
    }

    private isFinished(piece: FabPiece): boolean {
        return this.activeSteps.length > 0 && !piece.nextStepId
    }

    private isInArchivedView(piece: FabPiece): boolean {
        return !!piece.archived || this.isFinished(piece)
    }

    private applyPieceVisibility(savedScroll?: Point | null, savedPieceId?: string) {
        this.grid.items = this.allPieces.filter(p => (this.showArchived ? this.isInArchivedView(p) : !this.isInArchivedView(p)))
        this.applyJobGrouping(savedScroll, savedPieceId)
        this.updateHoldActionLabel()
    }

    releaseHold() {
        const piece = this.selectedPiece()
        if (!piece) {
            this.services.notification.error('Select a piece first.')
            return
        }
        if (!this.canPlaceHold) {
            this.services.notification.error('You do not have permission to release holds.')
            return
        }
        if (!piece.isOnHold || !piece.openHoldId) {
            this.services.notification.error('Select a piece that is on hold.')
            return
        }

        const label = piece.markCode || piece.name || 'this piece'
        this.services.modals
            .confirm('Release Hold', `Release hold on ${label}?`)
            .then(() => {
                const body: FabHoldReleaseRequest = {
                    fabHoldId: piece.openHoldId!,
                    releasedOn: new Date(),
                    releaseNote: null,
                }
                this.services.data.post('api/FabHold/Release', body).subscribe({
                    next: () => {
                        this.services.notification.success('Hold released.')
                        this.refresh()
                    },
                    error: () => this.services.notification.error('Failed to release hold.'),
                })
            })
            .catch(() => undefined)
    }

    refresh(showSpinner = true) {
        const requestedJobId = this.selectedJobId
        const savedScroll = this.savedScrollForRefresh(requestedJobId)
        const savedPieceId = savedScroll ? (this.grid.selectedItem as FabPiece | undefined)?.id : undefined
        let params = new HttpParams()
        if (requestedJobId) {
            params = params.set('fabJobId', requestedJobId)
        }

        this.cancelableRequest(
            '',
            showSpinner,
            this.services.data.load(showSpinner, showSpinner, this.services.data.get<FabBoard>('api/FabBoard', params)).subscribe({
                next: result => {
                    this.initialLoadingComplete()
                    const board = result[0]
                    this.jobs = board.jobs || []
                    this.processSteps = board.processSteps || []
                    this.activeSteps = this.processSteps.filter(s => s.active).sort((a, b) => a.sortOrder - b.sortOrder)
                    this.holdReasons = board.holdReasons || []

                    this.jobOptions = replaceListItemsIfChanged(this.jobOptions, [
                        new ListItem('', '(All jobs)'),
                        ...this.jobs.map(j => new ListItem(j.id, j.displayName || j.jobDescription || j.name || '')),
                    ])
                    this.appliedJobId = requestedJobId
                    if (this.selectedJobId !== requestedJobId) {
                        this.selectedJobId = requestedJobId
                    }

                    const jobIndexById = new Map(this.jobs.map((j, i) => [j.id, i]))
                    this.allPieces = (board.pieces || []).map(p => this.flattenPiece(p, jobIndexById))
                    this.applyPieceVisibility(savedScroll, savedPieceId)
                },
            }),
        )
    }

    private flattenPiece(piece: FabPiece, jobIndexById: Map<string, number>): FabPiece {
        const row = piece as FabPiece & Record<string, unknown>
        const stepsById = new Map((piece.steps || []).map(s => [s.fabProcessStepId, s]))
        for (const step of this.activeSteps) {
            const match = stepsById.get(step.id)
            const notApplicable = !!match?.isNotApplicable
            row['stepNa_' + step.id] = notApplicable
            row['step_' + step.id] = !notApplicable && match?.actualDate ? new Date(match.actualDate) : null
            row['stepRisk_' + step.id] = !notApplicable && !!match?.isAtRisk
        }
        const jobIndex = jobIndexById.get(piece.fabJobId)
        const job = jobIndex != null ? this.jobs[jobIndex] : undefined
        piece.jobDisplayName = this.jobLabel(job)
        piece.jobSortOrder = jobIndex != null ? jobIndex : Number.MAX_SAFE_INTEGER
        piece.imageUrls = piece.imageUrls || []
        piece.imageFileNames = piece.imageFileNames || []
        piece.drawingFileNames = piece.imageFileNames.join(', ')
        return piece
    }

    syncPieceDrawings(pieceId: string, urls: string[], names: string[]) {
        const row = this.grid.items.find(p => p.id === pieceId)
        if (!row) return
        row.imageUrls = [...urls]
        row.imageFileNames = [...names]
        row.drawingFileNames = names.join(', ')
        this.grid.flexGrid?.invalidate()
    }

    private jobLabel(job: FabJob | undefined): string {
        if (!job) return '(No job)'
        return job.displayName || job.jobDescription || job.name || '(No job)'
    }

    private savedScrollForRefresh(requestedJobId: string): Point | null {
        const flex = this.grid.flexGrid
        if (!this.grid.isInitialized || !flex || requestedJobId !== this.appliedJobId) return null

        const pos = flex.scrollPosition
        return new Point(pos.x, pos.y)
    }

    private applyJobGrouping(savedScroll?: Point | null, savedPieceId?: string) {
        if (!this.grid.isInitialized) return

        const view = this.grid.view
        view.beginUpdate()
        view.sortDescriptions.clear()
        view.sortDescriptions.push(new SortDescription('jobSortOrder', true))
        view.groupDescriptions.clear()
        view.groupDescriptions.push(new PropertyGroupDescription('jobDisplayName'))
        view.endUpdate()
        this.grid.flexGrid.groupHeaderFormat = '{name} ({count:n0} items)'

        if (savedScroll) {
            this.restoreScroll(savedScroll, savedPieceId)
        } else {
            this.scrollGridToTop()
        }
    }

    private restoreScroll(position: Point, pieceId?: string) {
        const flex = this.grid.flexGrid
        if (!flex) return

        if (pieceId) {
            const piece = this.grid.items.find(p => p.id === pieceId)
            if (piece) this.grid.selectedItem = piece
        }

        // After the grid's 10ms scrollIntoView from restoring the selected piece.
        setTimeout(() => {
            flex.scrollPosition = position
        }, 30)
    }

    private scrollGridToTop() {
        const flex = this.grid.flexGrid
        if (!flex) return

        this.grid.view.moveCurrentToPosition(0)
        flex.select(0, 0)
        setTimeout(() => {
            flex.scrollPosition = new Point(0, 0)
        }, 20)
    }

    formatItem(_s: any, e: FormatItemEventArgs) {
        if (e.panel.cellType !== CellType.Cell) return

        // Cells are recycled while scrolling, so clear colors before applying this row's styles.
        e.cell.classList.remove('red-cell', 'yellow-cell', 'green-cell')
        e.cell.style.backgroundColor = ''
        e.cell.style.color = ''

        const row = e.panel.rows[e.row]
        if (row instanceof GroupRow) {
            this.formatJobGroupHeader(e)
            return
        }

        const binding = e.panel.columns[e.col]?.binding
        const item = row?.dataItem as FabPiece & Record<string, unknown>
        if (!item || !binding) return

        if (binding === 'currentStepName' && item.currentStepColorHex) {
            e.cell.style.backgroundColor = item.currentStepColorHex
            e.cell.style.color = this.contrastText(item.currentStepColorHex)
        }

        if (binding === 'riskLevel') {
            if (item.riskLevel === 'AtRisk' || item.riskLevel === 'Blocked') e.cell.classList.add('red-cell')
            else if (item.riskLevel === 'DueSoon') e.cell.classList.add('yellow-cell')
            else if (item.riskLevel === 'Complete') e.cell.classList.add('green-cell')
        }

        if (binding?.startsWith('step_')) {
            const stepId = binding.substring(5)
            if (item['stepNa_' + stepId]) {
                e.cell.textContent = 'NA'
            } else if (item['stepRisk_' + stepId]) {
                e.cell.classList.add('red-cell')
            }
        }

        if (binding === 'holdReasonName' && item.isOnHold) {
            e.cell.classList.add('yellow-cell')
        }

        if (binding === 'description' && item.description) {
            e.cell.title = item.description
        }

        if (binding === 'drawingFileNames') {
            this.formatDrawingLinks(e, item)
        }
    }

    private formatDrawingLinks(e: FormatItemEventArgs, item: FabPiece) {
        const urls = item.imageUrls || []
        const names = item.imageFileNames || []
        e.cell.textContent = ''
        if (urls.length === 0) return

        urls.forEach((url, i) => {
            if (i > 0) e.cell.appendChild(document.createTextNode(', '))
            const docId = FabBoardComponent.documentIdFromStoredUrl(url)
            const label = names[i] || url.split('/').pop() || 'Drawing'
            if (!docId) {
                e.cell.appendChild(document.createTextNode(label))
                return
            }
            const link = document.createElement('a')
            link.href = `api/FabPiece/DownloadDocument?documentId=${encodeURIComponent(docId)}`
            link.target = '_blank'
            link.rel = 'noopener'
            link.textContent = label
            link.addEventListener('mousedown', ev => ev.stopPropagation())
            link.addEventListener('click', ev => {
                ev.preventDefault()
                ev.stopPropagation()
                window.open(link.href, '_blank', 'noopener')
            })
            e.cell.appendChild(link)
        })
    }

    private static documentIdFromStoredUrl(url: string): string | null {
        const parts = url.split('/').filter(Boolean)
        if (parts.length < 2) return null
        const file = parts[1]
        const dot = file.lastIndexOf('.')
        const idPart = dot > 0 ? file.slice(0, dot) : file
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idPart) ? idPart : null
    }

    private formatJobGroupHeader(e: FormatItemEventArgs) {
        const group = e.panel.rows[e.row]?.dataItem as CollectionViewGroup
        const collapse = e.cell.querySelector('.wj-elem-collapse')
        const text = e.cell.textContent || ''
        if (!collapse && !text.includes(String(group?.name ?? ''))) return

        const first = group?.items?.[0] as FabPiece | undefined
        const name = first?.jobDisplayName || '(No job)'
        const count = group?.items?.length ?? 0
        e.cell.textContent = ''
        if (collapse) {
            e.cell.appendChild(collapse)
        }
        e.cell.appendChild(document.createTextNode(` ${name} (${count} items)`))
    }

    private contrastText(hex: string): string {
        const h = (hex || '').replace('#', '')
        if (h.length < 6) return '#000'
        const r = parseInt(h.substring(0, 2), 16)
        const g = parseInt(h.substring(2, 4), 16)
        const b = parseInt(h.substring(4, 6), 16)
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
        return luminance > 0.6 ? '#000' : '#fff'
    }

    stepHeader(step: FabProcessStep): string {
        return step.name
    }

    stepBinding(step: FabProcessStep): string {
        return 'step_' + step.id
    }
}
